import { api, scopeId } from '../api.ts';
import type { Session } from '../api.ts';
import { badge, pageHeader, section } from '../components.ts';
import { empty, h, when } from '../ui.ts';

interface Pending {
  proposal: { id: string; title: string; detail: string; executor: string; scope: Session['scope']; sessionId: string };
  requestedAt: number;
}

/** Approvals: the human gate (SECURITY.md "Actions"). */
export async function approvalsView(): Promise<HTMLElement> {
  const pending = await api.get<Pending[]>('/approvals');
  const root = h('div', { class: 'page' }, pageHeader('Approvals', 'Nothing touches other people without a yes here.'));
  if (!pending.length) root.append(empty('No pending actions.'));
  for (const p of pending) root.append(approvalCard(p, async () => root.replaceWith(await approvalsView())));
  return root;
}

function approvalCard(p: Pending, refresh: () => Promise<void>): HTMLElement {
  const decide = async (approved: boolean) => {
    await api.post(`/approvals/${p.proposal.id}`, { approved, by: 'dashboard' });
    await refresh();
  };
  return h(
    'div',
    { class: 'card warning approval' },
    h('div', { class: 'card-head' }, h('span', { class: 'card-title' }, p.proposal.title), badge('warning', 'pending')),
    h('div', {}, p.proposal.detail),
    h(
      'div',
      { class: 'mono muted' },
      `executor ${p.proposal.executor} · ${scopeId(p.proposal.scope)} · from `,
      h('a', { class: 'link', href: `#/session/${p.proposal.sessionId}` }, p.proposal.sessionId),
      ` · ${when(p.requestedAt)}`,
    ),
    h(
      'div',
      { class: 'row' },
      h('button', { class: 'btn', type: 'button', onclick: () => void decide(true) }, 'Approve'),
      h('button', { class: 'btn outline danger-text', type: 'button', onclick: () => void decide(false) }, 'Reject'),
    ),
  );
}

/** Activity: everything the daemon emits, live, plus the session list. */
export async function activityView(): Promise<HTMLElement> {
  const sessions = await api.get<Session[]>('/sessions');
  const root = h(
    'div',
    { class: 'page' },
    pageHeader('Activity', 'Live feed of what she hears and does. Costs show per session.'),
  );
  const feed = h('div', { class: 'feed card' }, empty('Waiting for events…'));
  root.append(section('Live', feed));
  const off = api.events((name, payload) => {
    feed.querySelector('.empty')?.remove();
    feed.prepend(feedLine(name, payload as Record<string, unknown>));
    while (feed.children.length > 200) feed.lastChild?.remove();
  });
  window.addEventListener('hashchange', off, { once: true });
  root.append(section('Sessions', h('div', { class: 'card table-card' }, sessionsTable(sessions))));
  return root;
}

function feedLine(name: string, p: Record<string, unknown>): HTMLElement {
  const what =
    name === 'transcript.segment'
      ? `${p.speaker}: ${p.text}`
      : name === 'note.updated'
        ? `wrote ${(p.files as string[]).join(', ')} (${p.commit ?? 'no change'})`
        : name === 'channel.message'
          ? `she (${p.channel}): ${p.text}`
          : JSON.stringify(p).slice(0, 160);
  return h(
    'div',
    { class: 'ev' },
    h('span', { class: 'muted' }, new Date().toLocaleTimeString()),
    h('span', {}, name),
    h('span', {}, what),
  );
}

function sessionsTable(sessions: Session[]): HTMLElement {
  if (!sessions.length) return empty('No sessions yet.');
  const t = h(
    'table',
    {},
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        h('th', {}, 'started'),
        h('th', {}, 'title'),
        h('th', {}, 'scope'),
        h('th', {}, 'kind'),
        h('th', {}, 'segments'),
        h('th', {}, 'Live s'),
        h('th', {}, 'status'),
      ),
    ),
  );
  for (const s of sessions)
    t.append(
      h(
        'tr',
        { class: 'clickable', onclick: () => (location.hash = `#/session/${s.id}`) },
        h('td', { class: 'mono muted' }, when(s.startedAt)),
        h('td', {}, s.title),
        h('td', { class: 'mono' }, scopeId(s.scope)),
        h('td', {}, s.kind),
        h('td', {}, String(s.segments)),
        h('td', {}, s.live ? String(s.live.seconds) : '–'),
        h('td', {}, s.endedAt ? badge('outline', 'ended') : badge('success', 'live')),
      ),
    );
  return t;
}
